import React, { useState, useRef, useEffect } from 'react';
import { getApiUrl } from '../config/api';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBranch } from '../contexts/BranchContext';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, token } = useAuth();
  const { selectedBranchName, setSelectedBranch: setBranchContext, refreshTrigger } = useBranch();
  const [branches, setBranches] = useState(['All Branches']);
  const [branchIdMap, setBranchIdMap] = useState<{[key: string]: string}>({});

  // Function to generate initials from name (max 2 letters)
  const getInitials = (name: string | undefined) => {
    if (!name) return 'U';
    
    const words = name.trim().split(' ').filter(word => word.length > 0);
    if (words.length === 0) return 'U';
    
    if (words.length === 1) {
      // Single word: take first letter
      return words[0].charAt(0).toUpperCase();
    } else {
      // Multiple words: take first letter of first two words
      return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
    }
  };
  const [isBranchDropdownOpen, setIsBranchDropdownOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const profileDropdownRef = useRef<HTMLDivElement>(null);

  // Filter navigation items based on user role
  const getAllNavItems = () => [
    { name: 'Dashboard', path: '/' },
    { name: 'Inventory', path: '/inventory' },
    { name: 'Sales', path: '/sales' },
    { name: 'Dealers', path: '/dealers' },
    { name: 'Customers', path: '/customers' },
    { name: 'Expenses', path: '/expenses' },
    { name: 'Reports', path: '/reports' }
  ];

  const getFilteredNavItems = () => {
    const allItems = getAllNavItems();
    
    // Inventory managers can only access inventory
    if (user?.role === 'inventory_manager') {
      return allItems.filter(item => item.name === 'Inventory');
    }
    
    // Branch managers cannot access Reports and Settings
    if (user?.role === 'branch_manager') {
      return allItems.filter(item => item.name !== 'Reports');
    }
    
    // Admins can access everything
    return allItems;
  };

  const navItems = getFilteredNavItems();

  // Fetch branches from database
  useEffect(() => {
    console.log('Layout useEffect triggered, user:', user, 'token exists:', !!token);
    
    const fetchBranches = async () => {
      try {
        // Don't fetch if no token or no user
        if (!token || !user) {
          console.log('Layout: No token or user, setting default branches');
          setBranches([
            'All Branches',
            'Main Branch - Mumbai',
            'Branch 2 - Delhi',
            'Branch 3 - Bangalore',
            'Branch 4 - Chennai',
            'Branch 5 - Kolkata'
          ]);
          return;
        }

        console.log('Layout: Fetching branches for authenticated user:', user.name);

        const response = await fetch(getApiUrl("branches"), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        console.log('Branches API response status:', response.status);
        
        if (response.ok) {
          const data = await response.json();
          console.log('Branches API response data:', data);
          
          if (data.success) {
            // Handle case where branch manager has no valid branches
            if (!data.branches || data.branches.length === 0) {
              console.warn('No branches returned for user');
              if (user.role === 'branch_manager') {
                setBranches(['Invalid Branch Assignment']);
                setBranchContext(null, 'Invalid Branch Assignment');
                return;
              }
            }
            
            // Create branch ID to name mapping
            const idMap: {[key: string]: string} = {};
            data.branches.forEach((branch: any) => {
              idMap[branch.branch_name] = branch.id;
            });
            setBranchIdMap(idMap);
            
            if (data.branches) {
            
            // Filter branches based on user role
            let filteredBranches;
            
            if (user.role === 'branch_manager' && data.branches.length > 0) {
              // Branch managers get the first (and only) branch from their filtered results
              const branchName = data.branches[0].branch_name;
              const branchId = data.branches[0].id;
              console.log('Setting branch for manager:', { branchId, branchName });
              filteredBranches = [branchName];
              setBranchContext(branchId, branchName); // Set the only branch as default
            } else {
              // Admins and inventory managers see all branches with "All Branches" option
              filteredBranches = ['All Branches', ...data.branches.map((branch: any) => branch.branch_name)];
              // Keep "All Branches" as default for admins and inventory managers
            }
            
            console.log('Setting branches to:', filteredBranches);
            setBranches(filteredBranches);
            }
          } else {
            console.log('API response format issue:', data);
            setBranches([
              'All Branches',
              'Main Branch - Mumbai',
              'Branch 2 - Delhi',
              'Branch 3 - Bangalore',
              'Branch 4 - Chennai',
              'Branch 5 - Kolkata'
            ]);
          }
        } else {
          console.error('Failed to fetch branches, status:', response.status);
          const errorData = await response.text();
          console.error('Error response:', errorData);
          // Keep default branches on error - filtered by role
          if (user.role === 'branch_manager') {
            const defaultBranch = 'Main Branch - Mumbai';
            setBranches([defaultBranch]);
            setBranchContext('1', defaultBranch);
          } else {
            setBranches([
              'All Branches',
              'Main Branch - Mumbai',
              'Branch 2 - Delhi',
              'Branch 3 - Bangalore',
              'Branch 4 - Chennai',
              'Branch 5 - Kolkata'
            ]);
          }
        }
      } catch (error) {
        console.error('Error fetching branches:', error);
        // Keep default branches on error - filtered by role
        if (user && user.role === 'branch_manager') {
          const defaultBranch = 'Main Branch - Mumbai';
          setBranches([defaultBranch]);
          setBranchContext('1', defaultBranch);
        } else {
          setBranches([
            'All Branches',
            'Main Branch - Mumbai',
            'Branch 2 - Delhi',
            'Branch 3 - Bangalore',
            'Branch 4 - Chennai',
            'Branch 5 - Kolkata'
          ]);
        }
      }
    };

    fetchBranches();
  }, [user, token, setBranchContext, refreshTrigger]); // Add refreshTrigger as dependency

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsBranchDropdownOpen(false);
      }
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target as Node)) {
        setIsProfileDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleBranchSelect = (branch: string) => {
    // Get branch ID from the mapping
    const branchId = branch === 'All Branches' ? null : branchIdMap[branch];
    setBranchContext(branchId, branch);
    setIsBranchDropdownOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    setIsProfileDropdownOpen(false);
  };

  const handleProfileSettings = () => {
    // Navigate to settings page using React Router
    navigate('/settings');
    setIsProfileDropdownOpen(false);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo and Brand */}
            <div className="flex items-center">
              <Link 
                to="/" 
                className="flex items-center hover:opacity-80 transition-opacity"
                onClick={(e) => {
                  // Reset dashboard filters to default when logo is clicked
                  if (location.pathname === '/') {
                    e.preventDefault();
                    // Reset branch to "All Branches" for admins and inventory managers
                    if (user?.role !== 'branch_manager') {
                      setBranchContext(null, 'All Branches');
                    }
                    // Dispatch custom event to reset dashboard filters
                    window.dispatchEvent(new CustomEvent('resetDashboardFilters'));
                  }
                }}
              >
                <img 
                  src="/images/gadgetly-logo.jpg"
                  alt="Gadgetly Logo" 
                  className="h-8 w-auto"
                  onError={(e) => {
                    const img = e.currentTarget;
                    // Add a protective check to prevent hiding logo during print operations
                    const isPrintOperation = document.querySelector('[data-print-window]') !== null;
                    
                    if (isPrintOperation) {
                      // Don't hide logo during print operations
                      return;
                    }
                    
                    // Only hide if the image actually failed to load and retry once
                    if (!img.hasAttribute('data-retry')) {
                      img.setAttribute('data-retry', 'true');
                      // Force reload the image
                      img.src = img.src + '?t=' + Date.now();
                    } else {
                      // Only fallback to text after retry fails
                      img.style.display = 'none';
                      const fallback = img.nextElementSibling as HTMLElement;
                      if (fallback) fallback.style.display = 'block';
                    }
                  }}
                  onLoad={() => {
                    // Ensure logo stays visible after successful load
                    const img = document.querySelector('img[alt="iHub Logo"]') as HTMLImageElement;
                    if (img) {
                      img.style.display = 'block';
                      const fallback = img.nextElementSibling as HTMLElement;
                      if (fallback) fallback.style.display = 'none';
                    }
                  }}
                />
                <span className="ml-3 text-xl font-semibold text-gray-900" style={{display: 'none'}}>iHub Pulse</span>
              </Link>
            </div>

            {/* Navigation */}
            <nav className="hidden lg:flex space-x-2">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    location.pathname === item.path
                      ? 'bg-primary-500 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </nav>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>

            {/* Right side actions */}
            <div className="flex items-center space-x-4">
              {/* Branches dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button 
                  onClick={() => setIsBranchDropdownOpen(!isBranchDropdownOpen)}
                  className="flex items-center gap-2 p-2 text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  <span className="text-sm font-medium">{selectedBranchName}</span>
                  <svg className={`w-4 h-4 transition-transform ${isBranchDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {/* Dropdown menu */}
                {isBranchDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                    {branches.map((branch, index) => {
                      // Debug log for each branch
                      if (index === 0) console.log('Rendering branches dropdown with:', branches);
                      return (
                      <button
                        key={index}
                        onClick={() => handleBranchSelect(branch)}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                          selectedBranchName === branch ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-700'
                        }`}
                      >
                        {branch}
                      </button>
                      );
                    })}
                  </div>
                )}
              </div>
              
              {/* User Profile Dropdown */}
              <div className="relative" ref={profileDropdownRef}>
                <button
                  onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                  className="w-8 h-8 bg-black rounded-full flex items-center justify-center hover:bg-gray-800 transition-colors"
                >
                  <span className="text-sm font-medium text-white">
                    {getInitials(user?.name)}
                  </span>
                </button>

                {/* Profile Dropdown Menu */}
                {isProfileDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900">{user?.name || 'User'}</p>
                      <p className="text-xs text-gray-500 capitalize">{user?.role || 'No role'}</p>
                    </div>
                    
                    <button
                      onClick={handleProfileSettings}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Settings
                    </button>
                    
                    <div className="border-t border-gray-100 mt-1 pt-1">
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        Log Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-gray-200 bg-white">
            <nav className="px-4 py-2 space-y-1">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    location.pathname === item.path
                      ? 'bg-primary-500 text-white'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
    </div>
  );
};

export default Layout;